// Vendor
import PropTypes from 'prop-types';

// Components
import { ButtonColor } from 'src/components/atoms/Button';
import { ButtonLinkColor } from 'src/components/atoms/ButtonLink';
import Gap from 'src/components/atoms/Gap';
import Grid from 'src/components/atoms/Grid';
import { Line } from 'src/components/atoms/Line';
import Text, { TextAlignItems, TextVariant } from 'src/components/atoms/Text';
import CopyButton from 'src/components/molecules/CopyButton';
import { StyledButtonContainer, StyledContainer, StyledGrid } from './styles';

// Helpers
import { getLeasingTeamLink, getPublicLink } from 'src/helpers/getDUPLink';

// Types
import OutboundIntegrationLogo from 'src/components/molecules/OutboundIntegrationLogo/OutboundIntegrationLogo';
import useHideDocLinks from 'src/features/fdeInbox/hooks/useHideDocLinks';
import useHidePortalLinks from 'src/features/propertyFeature/hooks/useHidePortalLinks';
import { Feature, FeatureState } from 'src/ts/enums';
import { IProperty } from 'src/ts/interfaces';

export type DocumentLinkRowProps = Pick<
  IProperty,
  | 'id'
  | 'name'
  | 'address'
  | 'city'
  | 'state'
  | 'zip'
  | 'short_id'
  | 'company_short_id'
  | 'disable_dup_fd'
  | 'property_features'
>;

const DocumentLinkRow: React.FC<DocumentLinkRowProps> = (props) => {
  const {
    company_short_id,
    id: propertyId,
    short_id: propertyShortId,
    disable_dup_fd: idDupDisabled,
    property_features
  } = props;
  const { hideDocumentLinks } = useHideDocLinks();
  const { hidePropertyLinks } = useHidePortalLinks();

  // Applicant-experience bounces the on-behalf-of flow back to the applicant
  // flow when this feature is off, so hide the link rather than send agents there.
  const isOnBehalfOfEnabled =
    property_features?.some(
      (feature) => feature.code === Feature.ON_BEHALF_OF && feature.state === FeatureState.ENABLED
    ) === true;

  if (idDupDisabled || hideDocumentLinks || hidePropertyLinks(property_features)) {
    return null;
  }

  return (
    <>
      <StyledContainer>
        <Line height="tiny" />
        <Gap height={1} />
        <StyledGrid direction="column">
          <Grid justify="space-between">
            <Text isBold alignItems={TextAlignItems.center} color={'initial'}>
              {props.name}
            </Text>
            <OutboundIntegrationLogo propertyId={propertyId} />
          </Grid>
          <Gap height={0.1875} />
          <Text variant={TextVariant.small} color={'gray700'}>
            {`${props.address}, ${props.city}, ${props.state} ${props.zip}`}
          </Text>
          <Gap height={1} />
          <StyledButtonContainer>
            <CopyButton
              to={getPublicLink(propertyShortId || '', company_short_id || '')}
              label="Applicants Link"
              showCopyButton
            />
            {isOnBehalfOfEnabled && (
              <CopyButton
                to={getLeasingTeamLink(propertyShortId || '', company_short_id || '')}
                label="Leasing Team Link"
                colorLink={ButtonLinkColor.secondary}
                colorCopy={ButtonColor.secondary}
              />
            )}
          </StyledButtonContainer>
        </StyledGrid>
      </StyledContainer>
    </>
  );
};

DocumentLinkRow.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  city: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  zip: PropTypes.string.isRequired,
  short_id: PropTypes.string,
  company_short_id: PropTypes.string.isRequired,
  disable_dup_fd: PropTypes.bool,
  property_features: PropTypes.array
};

export default DocumentLinkRow;
